import { useLocalization } from "@fluent/react"
import {
    Box,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material"
import { alpha, lighten, useTheme } from "@mui/material/styles"
import CheckRoundedIcon from "@mui/icons-material/CheckRounded"

export type AreaRatingValue = "GREEN" | "YELLOW" | "RED"

export const ratingDotColors = {
    GREEN: { color: "#5BB85D", borderColor: "#35C719" },
    YELLOW: { color: "#FFBE32", borderColor: "#FFB800" },
    RED: { color: "#ED1C24", borderColor: "#C81020" },
} as const

type AreaRatingSelectorProps = {
    rating: string | null
    onChange: (rating: AreaRatingValue) => void
    intensive?: boolean
    showLabel?: boolean
    disabled?: boolean
    saving?: boolean
    previewOnly?: boolean

    // Zachowane dla dotychczasowych wywołań komponentu.
    compact?: boolean
}

type RatingOption = {
    value: AreaRatingValue
    label: string
    color: string
    borderColor: string
    contrastText: string
}

export const AreaRatingSelector = ({
    rating,
    onChange,
    intensive = false,
    compact = false,
    showLabel = !compact,
    disabled = false,
    saving = false,
    previewOnly = false,
}: AreaRatingSelectorProps) => {
    const theme = useTheme()
    const { l10n } = useLocalization()
    const isDark = theme.palette.mode === "dark"

    const label = (key: string, fallback: string) =>
        l10n.getString(
            `operational-dashboard-${key}`,
            null,
            `_${fallback}`
        )

    const previewLabel = label(
        "rating-preview",
        "Wybór tymczasowy, bez zapisu"
    )

    const options: RatingOption[] = [
        {
            value: "GREEN",
            label: label("rating-green", "Pod kontrolą"),
            color: intensive ? "#00BD60" : ratingDotColors.GREEN.color,
            borderColor: intensive ? "#00C853" : ratingDotColors.GREEN.borderColor,
            contrastText: "#052E16",
        },
        {
            value: "YELLOW",
            label: label("rating-yellow", "Wymaga uwagi"),
            color: intensive ? "#FFCF00" : ratingDotColors.YELLOW.color,
            borderColor: intensive ? "#FFD600" : ratingDotColors.YELLOW.borderColor,
            contrastText: "#422006",
        },
        {
            value: "RED",
            label: label("rating-red", "Krytyczny"),
            color: intensive ? "#FF1B26" : ratingDotColors.RED.color,
            borderColor: intensive ? "#D90018" : ratingDotColors.RED.borderColor,
            contrastText: "#450A0A",
        },
    ]

    const normalizedRating = rating?.toUpperCase()

    const selected = options.find(
        option => option.value === normalizedRating
    )

    const statusLabel =
        selected?.label ??
        (normalizedRating === "NO_MEETING"
            ? label("rating-no-meeting", "Brak spotkania")
            : normalizedRating === "NO_AGENDA"
                ? label("outside-agenda", "Poza agendą")
                : l10n.getString("not-assessed", null, "_Nie oceniono"))

    const blocked = disabled || saving

    const labelBackground = selected
        ? alpha(selected.color, isDark ? 0.2 : 0.18)
        : alpha(theme.palette.text.primary, 0.06)

    const labelColor = selected
        ? isDark
            ? lighten(selected.color, 0.6)
            : selected.contrastText
        : theme.palette.text.secondary

    return (
        <Stack
            direction="column"
            alignItems="center"
            spacing={0.75}
            sx={{
                display: "inline-flex",
                verticalAlign: "middle",
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                role="group"
                aria-label={
                    previewOnly
                        ? previewLabel
                        : label("rating-status", "Status obszaru")
                }
                aria-busy={saving}
                sx={{
                    gap: 0.25,
                    p: 0,
                    border: 0,
                    bgcolor: "transparent",
                }}
            >
                {options.map(option => {
                    const isSelected = selected?.value === option.value

                    return (
                        <Tooltip
                            key={option.value}
                            title={
                                previewOnly
                                    ? `${option.label} — ${previewLabel}`
                                    : option.label
                            }
                        >
                            <Box
                                component="span"
                                sx={{ display: "inline-flex" }}
                            >
                                <IconButton
                                    size="small"
                                    disabled={blocked}
                                    aria-label={option.label}
                                    aria-pressed={isSelected}
                                    onClick={() => {
                                        if (!isSelected) {
                                            onChange(option.value)
                                        }
                                    }}
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        p: 0,
                                        borderRadius: 1,

                                        "&:hover": {
                                            bgcolor: alpha(option.color, 0.12),
                                        },

                                        "&.Mui-focusVisible": {
                                            outline: "2px solid",
                                            outlineColor: "text.primary",
                                            outlineOffset: 1,
                                        },

                                        "@media (pointer: coarse)": {
                                            width: 44,
                                            height: 44,
                                        },
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            width: isSelected ? 14 : 10,
                                            height: isSelected ? 14 : 10,
                                            boxSizing: "border-box",
                                            flexShrink: 0,
                                            borderRadius: "50%",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",

                                            bgcolor: isSelected
                                                ? option.color
                                                : alpha(
                                                    option.color,
                                                    isDark ? 0.2 : 0.14
                                                ),

                                            color: option.contrastText,

                                            border: isSelected
                                                ? "none"
                                                : `1px solid ${alpha(
                                                    option.borderColor,
                                                    0.3
                                                )}`,

                                            outline: isSelected
                                                ? `2px solid ${option.borderColor}`
                                                : "none",

                                            outlineOffset: 2,
                                            boxShadow: "none",

                                            transition:
                                                "background-color 150ms ease, outline-color 150ms ease, width 150ms ease, height 150ms ease",
                                        }}
                                    >
                                         
                                    </Box>
                                </IconButton>
                            </Box>
                        </Tooltip>
                    )
                })}
            </Stack>

            {showLabel && (
                <Typography
                    component="span"
                    variant="caption"
                    aria-live="polite"
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 1.125,
                        py: 0.5,
                        borderRadius: 1.5,

                        bgcolor: labelBackground,
                        color: labelColor,

                        fontSize: 11,
                        fontWeight: 500,
                        lineHeight: 1.3,
                        whiteSpace: "nowrap",
                        textAlign: "center",

                        transition:
                            "background-color 150ms ease, color 150ms ease",
                    }}
                >
                    {saving
                        ? l10n.getString("saving", null, "_Zapisywanie…")
                        : statusLabel}
                </Typography>
            )}
        </Stack>
    )
}